import { ApiService } from './api';

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';

export interface FinancialStats {
  totalRevenue: number;
  monthlyRevenue: number;
  pendingInvoices: number;
  overdueInvoices: number;
  outstandingAmount: number;
  lastUpdated?: string;
}

export interface Pagination {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  clientId: number;
  clientName?: string | null;
  projectId?: number | null;
  projectName?: string | null;
  amount: number;
  balanceDue: number;
  status: InvoiceStatus;
  issueDate: string | null;
  dueDate: string | null;
  paidDate?: string | null;
  description?: string | null;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Payment {
  id: number;
  invoiceId: number;
  invoiceNumber?: string | null;
  clientName?: string | null;
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  paymentMethod: 'bank_transfer' | 'credit_card' | 'cash' | 'ach' | 'other';
  transactionId?: string | null;
  paymentDate?: string | null;
  created_at?: string;
}

export interface FinancialAnalytics {
  revenueByMonth: Array<{ month: string; revenue: number }>;
  topClients: Array<{ name: string; revenue: number }>;
  invoiceStatusDistribution: Array<{ status: InvoiceStatus; count: number; percentage: number }>;
}

export interface InvoiceFilters {
  status?: InvoiceStatus | 'all';
  search?: string;
  page?: number;
  perPage?: number;
}

export interface InvoicePayload {
  invoice_number?: string;
  client_id: number;
  project_id?: number | null;
  amount: number;
  status?: InvoiceStatus;
  issue_date?: string | null;
  due_date?: string | null;
  paid_date?: string | null;
  description?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown>;
}

export class FinancialService {
  static async getStats(): Promise<FinancialStats> {
    const response = await ApiService.get<{ success: boolean; data: FinancialStats }>('/admin/financial/stats');

    if (!response.success) {
      throw new Error(response.message || 'Unable to load financial statistics');
    }

    return response.data;
  }

  static async getInvoices(filters: InvoiceFilters = {}): Promise<{ invoices: Invoice[]; pagination: Pagination }> {
    const params: Record<string, string | number> = {};

    if (filters.status && filters.status !== 'all') {
      params.status = filters.status;
    }

    if (filters.search) {
      params.search = filters.search;
    }

    if (filters.page) {
      params.page = filters.page;
    }

    if (filters.perPage) {
      params.per_page = filters.perPage;
    }

    const response = await ApiService.get<{
      success: boolean;
      data: {
        invoices: Invoice[];
        pagination: Pagination;
      };
    }>('/admin/financial/invoices', { params });

    if (!response.success) {
      throw new Error(response.message || 'Unable to load invoices');
    }

    return response.data;
  }

  static async getInvoice(id: number): Promise<{ invoice: Invoice; payments: Payment[] }> {
    const response = await ApiService.get<{
      success: boolean;
      data: { invoice: Invoice; payments: Payment[] };
    }>(`/admin/financial/invoices/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Unable to load invoice');
    }

    return response.data;
  }

  static async createInvoice(payload: InvoicePayload): Promise<Invoice> {
    const response = await ApiService.post<{
      success: boolean;
      message: string;
      data: Invoice;
    }>('/admin/financial/invoices', payload);

    if (!response.success) {
      throw new Error(response.message || 'Unable to create invoice');
    }

    return response.data;
  }

  static async updateInvoice(id: number, payload: InvoicePayload): Promise<Invoice> {
    const response = await ApiService.put<{
      success: boolean;
      message: string;
      data: Invoice;
    }>(`/admin/financial/invoices/${id}`, payload);

    if (!response.success) {
      throw new Error(response.message || 'Unable to update invoice');
    }

    return response.data;
  }

  static async deleteInvoice(id: number): Promise<void> {
    const response = await ApiService.delete<{ success: boolean; message: string }>(`/admin/financial/invoices/${id}`);

    if (!response.success) {
      throw new Error(response.message || 'Unable to delete invoice');
    }
  }

  static async getPayments(params: { status?: Payment['status'] | 'all'; page?: number; perPage?: number } = {}): Promise<{
    payments: Payment[];
    pagination: Pagination;
  }> {
    const query: Record<string, string | number> = {};

    if (params.status && params.status !== 'all') {
      query.status = params.status;
    }

    if (params.page) {
      query.page = params.page;
    }

    if (params.perPage) {
      query.per_page = params.perPage;
    }

    const response = await ApiService.get<{
      success: boolean;
      data: {
        payments: Payment[];
        pagination: Pagination;
      };
    }>('/admin/financial/payments', { params: query });

    if (!response.success) {
      throw new Error(response.message || 'Unable to load payments');
    }

    return response.data;
  }

  static async createPayment(payload: {
    invoice_id: number;
    amount: number;
    payment_method: Payment['paymentMethod'];
    payment_date: string;
    transaction_id?: string | null;
    notes?: string | null;
    status?: Payment['status'];
  }): Promise<Payment> {
    const response = await ApiService.post<{
      success: boolean;
      message: string;
      data: Payment;
    }>('/admin/financial/payments', payload);

    if (!response.success) {
      throw new Error(response.message || 'Unable to create payment');
    }

    return response.data;
  }

  static async getAnalytics(): Promise<FinancialAnalytics> {
    const response = await ApiService.get<{
      success: boolean;
      data: FinancialAnalytics;
    }>('/admin/financial/analytics');

    if (!response.success) {
      throw new Error(response.message || 'Unable to load financial analytics');
    }

    return response.data;
  }
}

export default FinancialService;
